/**
 * Progressive WebGL upgrade for the atom mark.
 *
 * The CSS atom paints immediately and stays the fallback: it is what shows
 * before this module runs, if WebGL is unavailable, if the GL context is lost,
 * or if anything here throws. Only once a real frame has been drawn does the
 * host swap the two over.
 *
 * This is hand-rolled rather than built on a 3D library because the scene is
 * three tori and eighteen spheres — a library sized for arbitrary scenes would
 * cost roughly fifty times more bytes than the whole renderer below, on a site
 * whose pitch is that it loads fast.
 *
 * Motion is kept in lockstep with the CSS version's keyframes (shell radii,
 * precession periods, tilt drift, circulation periods) so the handover is not
 * visible beyond the crossfade.
 */

/** @typedef {{positions: number[], normals: number[], indices: number[]}} Geometry */
/** @typedef {{position: WebGLBuffer, normal: WebGLBuffer, index: WebGLBuffer, count: number}} Mesh */
/** @typedef {{radius: number, electrons: number, precession: number, spin: number, spinSign: number, axis: string}} Shell */

const VERTEX_SHADER = `
  attribute vec3 aPosition;
  attribute vec3 aNormal;

  uniform mat4 uProjection;
  uniform mat4 uView;
  uniform mat4 uModel;

  varying vec3 vNormal;
  varying vec3 vWorld;

  void main() {
    vec4 world = uModel * vec4(aPosition, 1.0);
    vWorld = world.xyz;
    // Model matrices here are rotation + uniform scale only, so the upper 3x3
    // is a similarity transform and needs no inverse-transpose.
    vNormal = mat3(uModel) * aNormal;
    gl_Position = uProjection * uView * world;
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;

  varying vec3 vNormal;
  varying vec3 vWorld;

  uniform vec3 uCamera;
  uniform vec3 uTint;

  /**
   * Stand-in for a studio environment. Polished metal reads as metal because it
   * mirrors its surroundings, so shading from the reflection vector rather than
   * from light dot normal is what sells it — a plain diffuse term would look
   * like dark plastic no matter how the highlights were tuned.
   */
  vec3 environment(vec3 direction) {
    float horizon = smoothstep(-0.35, 0.75, direction.y);
    vec3 base = mix(vec3(0.015), vec3(0.62), horizon);

    // Key softbox, high and to the left.
    vec3 key = normalize(vec3(-0.45, 0.85, 0.55));
    base += vec3(1.0) * pow(max(dot(direction, key), 0.0), 90.0) * 1.35;
    base += vec3(0.55) * pow(max(dot(direction, key), 0.0), 14.0) * 0.5;

    // Cooler fill from the lower right, so the shadow side never goes flat.
    vec3 fill = normalize(vec3(0.75, -0.25, 0.45));
    base += vec3(0.30, 0.33, 0.38) * pow(max(dot(direction, fill), 0.0), 10.0);

    return base;
  }

  void main() {
    vec3 normal = normalize(vNormal);
    vec3 view = normalize(uCamera - vWorld);

    // Two-sided: the far wall of a tube shows its inside surface.
    if (!gl_FrontFacing) normal = -normal;

    vec3 reflection = reflect(-view, normal);

    // Grazing angles reflect more strongly than head-on ones.
    float fresnel = pow(1.0 - clamp(dot(normal, view), 0.0, 1.0), 3.5);

    vec3 colour = uTint * environment(reflection);
    colour += vec3(0.55, 0.58, 0.62) * fresnel * 0.5;

    gl_FragColor = vec4(colour, 1.0);
  }
`;

/* ------------------------------------------------------------------ */
/* Column-major 4x4 matrices, matching WebGL's expected memory layout. */
/* ------------------------------------------------------------------ */

/** @returns {Float32Array} */
function identity() {
  // prettier-ignore
  return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
}

/**
 * @param {Float32Array} a
 * @param {Float32Array} b
 * @returns {Float32Array} the product a * b
 */
function multiply(a, b) {
  const out = new Float32Array(16);

  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[col * 4 + k];
      out[col * 4 + row] = sum;
    }
  }

  return out;
}

/**
 * @param {number} radians
 * @returns {Float32Array}
 */
function rotationX(radians) {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  // prettier-ignore
  return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
}

/**
 * @param {number} radians
 * @returns {Float32Array}
 */
function rotationY(radians) {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  // prettier-ignore
  return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]);
}

/**
 * @param {number} radians
 * @returns {Float32Array}
 */
function rotationZ(radians) {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  // prettier-ignore
  return new Float32Array([c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @returns {Float32Array}
 */
function translation(x, y, z) {
  const out = identity();
  out[12] = x;
  out[13] = y;
  out[14] = z;
  return out;
}

/**
 * @param {number} s - Uniform scale factor.
 * @returns {Float32Array}
 */
function scaling(s) {
  const out = identity();
  out[0] = out[5] = out[10] = s;
  return out;
}

/**
 * @param {number} fovY - Vertical field of view, in radians.
 * @param {number} aspect
 * @param {number} near
 * @param {number} far
 * @returns {Float32Array}
 */
function perspective(fovY, aspect, near, far) {
  const f = 1 / Math.tan(fovY / 2);
  const range = 1 / (near - far);
  const out = new Float32Array(16);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) * range;
  out[11] = -1;
  out[14] = 2 * far * near * range;
  return out;
}

/* ---------------------------------------------- */
/* Parametric geometry, generated as unit meshes. */
/* ---------------------------------------------- */

/**
 * A torus lying in the XY plane with its axis along Z, matching how the CSS
 * shells are oriented so the same transforms drive both.
 *
 * @param {number} ringRadius - Distance from the centre to the tube's centreline.
 * @param {number} tubeRadius
 * @param {number} majorSegments - Divisions around the ring.
 * @param {number} minorSegments - Divisions around the tube's cross-section.
 * @returns {Geometry}
 */
function buildTorus(ringRadius, tubeRadius, majorSegments, minorSegments) {
  /** @type {number[]} */
  const positions = [];
  /** @type {number[]} */
  const normals = [];
  /** @type {number[]} */
  const indices = [];

  for (let i = 0; i <= majorSegments; i++) {
    const u = (i / majorSegments) * Math.PI * 2;
    const cosU = Math.cos(u);
    const sinU = Math.sin(u);

    for (let j = 0; j <= minorSegments; j++) {
      const v = (j / minorSegments) * Math.PI * 2;
      const cosV = Math.cos(v);
      const sinV = Math.sin(v);

      // Surface normal points out from the centre of the tube's cross-section.
      const nx = cosV * cosU;
      const ny = cosV * sinU;
      const nz = sinV;

      normals.push(nx, ny, nz);
      positions.push(ringRadius * cosU + tubeRadius * nx, ringRadius * sinU + tubeRadius * ny, tubeRadius * nz);
    }
  }

  const stride = minorSegments + 1;
  for (let i = 0; i < majorSegments; i++) {
    for (let j = 0; j < minorSegments; j++) {
      const a = i * stride + j;
      const b = a + stride;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  return { positions, normals, indices };
}

/**
 * Unit sphere; instances scale it to core or electron size.
 *
 * @param {number} segmentsU
 * @param {number} segmentsV
 * @returns {Geometry}
 */
function buildSphere(segmentsU, segmentsV) {
  /** @type {number[]} */
  const positions = [];
  /** @type {number[]} */
  const normals = [];
  /** @type {number[]} */
  const indices = [];

  for (let i = 0; i <= segmentsV; i++) {
    const theta = (i / segmentsV) * Math.PI;
    const sinT = Math.sin(theta);
    const cosT = Math.cos(theta);

    for (let j = 0; j <= segmentsU; j++) {
      const phi = (j / segmentsU) * Math.PI * 2;
      const x = sinT * Math.cos(phi);
      const y = cosT;
      const z = sinT * Math.sin(phi);

      normals.push(x, y, z);
      positions.push(x, y, z);
    }
  }

  const stride = segmentsU + 1;
  for (let i = 0; i < segmentsV; i++) {
    for (let j = 0; j < segmentsU; j++) {
      const a = i * stride + j;
      const b = a + stride;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  return { positions, normals, indices };
}

/* ------------------------------------------------------------------ */
/* Scene definition — mirrors the CSS keyframes in snippets/atom-model */
/* ------------------------------------------------------------------ */

/** @type {Shell[]} */
const SHELLS = [
  // radius is in electron-size units; precession/spin are seconds per cycle.
  { radius: 2.5, electrons: 2, precession: 252, spin: 9, spinSign: 1, axis: 'y' },
  { radius: 4.5, electrons: 8, precession: 324, spin: 16, spinSign: -1, axis: 'x' },
  { radius: 6.5, electrons: 6, precession: 396, spin: 23, spinSign: 1, axis: 'z' },
];

/** Total precession sweep and tilt drift per cycle, matching the CSS. */
const PRECESSION_SWEEP = 12960;
const TILT_START = 45;
const TILT_DRIFT = 360;

/** CSS `perspective` on the atom container, so both projections agree. */
const PERSPECTIVE = 1200;

/** Hoisted out of the draw loop; these never change. */
const CORE_TINT = new Float32Array([0.42, 0.43, 0.45]);
const ELECTRON_TINT = new Float32Array([0.55, 0.56, 0.58]);
const RING_TINT = new Float32Array([0.62, 0.63, 0.66]);

const DEGREES = Math.PI / 180;

/**
 * Orientation of a shell at time `seconds`: the plane tilts on one axis while
 * the whole tilted plane revolves about a different, fixed axis.
 *
 * @param {Shell} shell
 * @param {number} seconds
 * @returns {Float32Array}
 */
function shellOrientation(shell, seconds) {
  const progress = (seconds % shell.precession) / shell.precession;
  const sweep = PRECESSION_SWEEP * progress * DEGREES;
  const tilt = (TILT_START + TILT_DRIFT * progress) * DEGREES;

  if (shell.axis === 'y') return multiply(rotationY(sweep), rotationX(tilt));
  if (shell.axis === 'x') return multiply(rotationX(sweep), rotationY(tilt));
  return multiply(rotationZ(sweep), rotationX(tilt));
}

/**
 * How far the electrons have circulated around their own ring.
 *
 * @param {Shell} shell
 * @param {number} seconds
 * @returns {number} radians
 */
function circulation(shell, seconds) {
  return shell.spinSign * 360 * ((seconds % shell.spin) / shell.spin) * DEGREES;
}

class AtomModel3D extends HTMLElement {
  /** @type {WebGLRenderingContext | null} */
  #gl = null;

  /** @type {HTMLCanvasElement | null} */
  #canvas = null;

  /** @type {WebGLProgram | null} */
  #program = null;

  /** @type {{sphere: Mesh, tori: Mesh[]} | null} */
  #meshes = null;

  /** @type {{projection: WebGLUniformLocation | null, view: WebGLUniformLocation | null, model: WebGLUniformLocation | null, camera: WebGLUniformLocation | null, tint: WebGLUniformLocation | null} | null} */
  #uniforms = null;

  /** @type {{position: number, normal: number} | null} */
  #attributes = null;

  /** @type {HTMLElement | null} */
  #host = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  /** @type {Float32Array | null} */
  #projection = null;

  /** @type {Float32Array | null} */
  #view = null;

  /** @type {Float32Array | null} */
  #camera = null;

  #frame = 0;
  #visible = true;
  #unit = 0;
  #started = false;

  connectedCallback() {
    this.#host = /** @type {HTMLElement | null} */ (this.closest('.atom-model'));
    if (!this.#host) return;

    try {
      this.#init();
    } catch (error) {
      // Any failure leaves the CSS atom in place, which is a complete mark.
      console.warn('[atom-model-3d] falling back to CSS', error);
      this.#teardown();
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #init() {
    this.#unit = this.#measureUnit();
    if (!this.#unit) return;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    this.appendChild(canvas);
    this.#canvas = canvas;

    const gl = /** @type {WebGLRenderingContext | null} */ (
      canvas.getContext('webgl', {
        alpha: true,
        antialias: true,
        premultipliedAlpha: true,
        depth: true,
      })
    );

    if (!gl) {
      canvas.remove();
      this.#canvas = null;
      return;
    }

    this.#gl = gl;
    canvas.addEventListener('webglcontextlost', this.#handleContextLost);

    const program = this.#buildProgram(gl);
    this.#program = program;

    this.#attributes = {
      position: gl.getAttribLocation(program, 'aPosition'),
      normal: gl.getAttribLocation(program, 'aNormal'),
    };

    this.#uniforms = {
      projection: gl.getUniformLocation(program, 'uProjection'),
      view: gl.getUniformLocation(program, 'uView'),
      model: gl.getUniformLocation(program, 'uModel'),
      camera: gl.getUniformLocation(program, 'uCamera'),
      tint: gl.getUniformLocation(program, 'uTint'),
    };

    this.#buildMeshes(gl);

    gl.enable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);

    this.#resize();
    this.#observeSize();
    this.#observeVisibility();

    this.#render();
  }

  /**
   * Reads the electron diameter straight off the CSS atom, so the responsive
   * clamp in the stylesheet stays the single source of truth for scale.
   * offsetWidth is used rather than a bounding rect because the electron sits
   * under 3D transforms that would otherwise foreshorten the measurement.
   *
   * @returns {number}
   */
  #measureUnit() {
    const probe = /** @type {HTMLElement | null} */ (this.#host?.querySelector('.atom-model__electron') ?? null);
    return probe ? probe.offsetWidth : 0;
  }

  /**
   * @param {WebGLRenderingContext} gl
   * @returns {WebGLProgram}
   */
  #buildProgram(gl) {
    const program = gl.createProgram();
    if (!program) throw new Error('could not create program');

    /** @type {[number, string][]} */
    const stages = [
      [gl.VERTEX_SHADER, VERTEX_SHADER],
      [gl.FRAGMENT_SHADER, FRAGMENT_SHADER],
    ];

    for (const [type, source] of stages) {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('could not create shader');

      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? 'shader compile failed');
      }

      gl.attachShader(program, shader);
      gl.deleteShader(shader);
    }

    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program) ?? 'program link failed');
    }

    return program;
  }

  /**
   * @param {WebGLRenderingContext} gl
   * @param {Geometry} geometry
   * @returns {Mesh}
   */
  #upload(gl, geometry) {
    const position = gl.createBuffer();
    const normal = gl.createBuffer();
    const index = gl.createBuffer();
    if (!position || !normal || !index) throw new Error('could not create buffer');

    gl.bindBuffer(gl.ARRAY_BUFFER, position);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry.positions), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, normal);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry.normals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, index);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(geometry.indices), gl.STATIC_DRAW);

    return { position, normal, index, count: geometry.indices.length };
  }

  /** @param {WebGLRenderingContext} gl */
  #buildMeshes(gl) {
    const unit = this.#unit;
    const tubeRadius = unit * 0.25;

    this.#meshes = {
      sphere: this.#upload(gl, buildSphere(32, 24)),
      tori: SHELLS.map((shell) => this.#upload(gl, buildTorus(shell.radius * unit, tubeRadius, 160, 28))),
    };
  }

  /**
   * @param {WebGLRenderingContext} gl
   * @param {Mesh} mesh
   * @param {Float32Array} model
   * @param {Float32Array} tint
   */
  #draw(gl, mesh, model, tint) {
    const attributes = this.#attributes;
    const uniforms = this.#uniforms;
    if (!attributes || !uniforms) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, mesh.position);
    gl.enableVertexAttribArray(attributes.position);
    gl.vertexAttribPointer(attributes.position, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, mesh.normal);
    gl.enableVertexAttribArray(attributes.normal);
    gl.vertexAttribPointer(attributes.normal, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.index);
    gl.uniformMatrix4fv(uniforms.model, false, model);
    gl.uniform3fv(uniforms.tint, tint);
    gl.drawElements(gl.TRIANGLES, mesh.count, gl.UNSIGNED_SHORT, 0);
  }

  #resize() {
    const gl = this.#gl;
    const canvas = this.#canvas;
    if (!gl || !canvas || !this.#host) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = this.clientWidth || this.#host.clientWidth;
    const height = this.clientHeight || this.#host.clientHeight;
    if (!width || !height) return;

    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Matching CSS `perspective`: the viewer sits PERSPECTIVE px from the z=0
    // plane, so the field of view follows from the element's height.
    const fov = 2 * Math.atan(height / 2 / PERSPECTIVE);
    this.#projection = perspective(fov, width / height, 1, PERSPECTIVE * 4);
    this.#view = translation(0, 0, -PERSPECTIVE);
    this.#camera = new Float32Array([0, 0, PERSPECTIVE]);
  }

  #observeSize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#observer = new ResizeObserver(() => {
      const gl = this.#gl;
      if (!gl) return;

      const unit = this.#measureUnit();

      // Radii are baked into the torus meshes, so a scale change needs them rebuilt.
      if (unit && unit !== this.#unit) {
        this.#unit = unit;
        this.#buildMeshes(gl);
      }

      this.#resize();
    });

    this.#observer.observe(this);
  }

  /** Idle offscreen rather than burning frames behind the fold. */
  #observeVisibility() {
    if (typeof IntersectionObserver === 'undefined') return;

    const visibility = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#visible = entry.isIntersecting;
    });

    visibility.observe(this);
  }

  #render = () => {
    const gl = this.#gl;
    if (!gl) return;

    this.#frame = requestAnimationFrame(this.#render);

    const meshes = this.#meshes;
    const uniforms = this.#uniforms;
    const projection = this.#projection;
    const view = this.#view;
    const camera = this.#camera;

    if (!this.#visible || !meshes || !uniforms || !projection || !view || !camera) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seconds = reduced ? 0 : performance.now() / 1000;
    const unit = this.#unit;

    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.useProgram(this.#program);
    gl.uniformMatrix4fv(uniforms.projection, false, projection);
    gl.uniformMatrix4fv(uniforms.view, false, view);
    gl.uniform3fv(uniforms.camera, camera);

    // Nucleus.
    this.#draw(gl, meshes.sphere, scaling(unit * 0.875), CORE_TINT);

    SHELLS.forEach((shell, index) => {
      const orientation = shellOrientation(shell, seconds);
      this.#draw(gl, meshes.tori[index], orientation, RING_TINT);

      const spin = circulation(shell, seconds);
      const electronScale = scaling(unit * 0.5);

      for (let i = 0; i < shell.electrons; i++) {
        const angle = spin + (Math.PI * 2 * i) / shell.electrons;
        const placement = multiply(rotationZ(angle), translation(shell.radius * unit, 0, 0));
        this.#draw(gl, meshes.sphere, multiply(multiply(orientation, placement), electronScale), ELECTRON_TINT);
      }
    });

    if (!this.#started) {
      this.#started = true;
      this.#host?.classList.add('atom-model--gl');
    }
  };

  /** @param {Event} event */
  #handleContextLost = (event) => {
    event.preventDefault();
    this.#teardown();
  };

  #teardown() {
    cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
    this.#host?.classList.remove('atom-model--gl');
    this.#canvas?.remove();
    this.#canvas = null;
    this.#gl = null;
    this.#started = false;
  }
}

if (!customElements.get('atom-model-3d')) {
  customElements.define('atom-model-3d', AtomModel3D);
}
